import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Trash2, X, FileText, MessageSquare, AlertCircle } from 'lucide-react';
import AppLayout from '@components/AppLayout';
import client from '../../src/api/client';
import '../styles/request-status.css';

const STATUS_META = {
  pending: { label: 'Pending', tone: 'pending' },
  in_progress: { label: 'In Progress', tone: 'progress' },
  resolved: { label: 'Resolved', tone: 'resolved' },
  rejected: { label: 'Rejected', tone: 'rejected' },
  deleted: { label: 'Deleted', tone: 'deleted' },
};

const PRIORITY_META = {
  urgent: { label: 'Urgent', tone: 'urgent' },
  high: { label: 'High', tone: 'high' },
  medium: { label: 'Medium', tone: 'medium' },
  low: { label: 'Low', tone: 'low' },
};

const normalizeStatus = (value) => {
  if (!value && value !== 0) {
    return 'pending';
  }
  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, '_');
  if (['deleted', 'removed'].includes(normalized)) {
    return 'deleted';
  }
  if (['resolved', 'closed', 'done', 'completed'].includes(normalized)) {
    return 'resolved';
  }
  if (['rejected', 'denied', 'cancelled', 'canceled'].includes(normalized)) {
    return 'rejected';
  }
  if (['in_progress', 'in_review', 'in-review', 'assigned', 'processing'].includes(normalized)) {
    return 'in_progress';
  }
  if (['open', 'pending', 'new'].includes(normalized)) {
    return 'pending';
  }
  return 'pending';
};

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toTitleCase = (value) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '';

export default function MyRequestsPage({ user, onLogout }) {
  const navigate = useNavigate();

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete' || ((user?.isApproved === 'pending' || user?.isApproved === 0 || user?.isApproved === false) && !user?.dateOfBirth);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, requestId: null, subject: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    let active = true;

    const fetchRequests = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await client.get(`/api/users/${user.id}/requests`);
        if (!active) {
          return;
        }
        const list = Array.isArray(response?.data?.requests) ? response.data.requests : [];
        setRequests(list);
      } catch (fetchError) {
        if (!active) {
          return;
        }
        setError('Unable to load your requests. Please try again.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchRequests();

    return () => {
      active = false;
    };
  }, [user?.id]);

  const items = useMemo(
    () =>
      requests.map((request) => {
        const statusKey = normalizeStatus(request.status);
        const meta = STATUS_META[statusKey] ?? STATUS_META.pending;
        const priorityKey = String(request.priority ?? '').trim().toLowerCase();
        const priorityMeta = PRIORITY_META[priorityKey] ?? null;
        return {
          ...request,
          statusKey,
          statusLabel: meta.label,
          statusTone: meta.tone,
          priorityLabel: priorityMeta?.label ?? '',
          priorityTone: priorityMeta?.tone ?? '',
        };
      }),
    [requests],
  );

  const openDeleteDialog = (request) => {
    setDeleteDialog({ open: true, requestId: request.requestId, subject: request.subject ?? '' });
    setDeleteError('');
  };

  const closeDeleteDialog = () => {
    if (deleteLoading) {
      return;
    }
    setDeleteDialog({ open: false, requestId: null, subject: '' });
    setDeleteError('');
  };

  const confirmDelete = async () => {
    if (!user?.id || !deleteDialog.requestId) {
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await client.delete(`/api/users/${user.id}/requests/${deleteDialog.requestId}`);
      setRequests((prev) =>
        prev.map((request) =>
          request.requestId === deleteDialog.requestId ? { ...request, status: 'deleted' } : request,
        ),
      );
      closeDeleteDialog();
    } catch (fetchError) {
      setDeleteError('Unable to delete request. Please try again.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (!isVerified) {
    return (
      <AppLayout title="My Requests" user={user} onLogout={onLogout}>
        <div className="request-status-page">
          <div className="verification-prompt" style={{
            padding: '4rem 2rem',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '2rem auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ color: '#f59e0b' }}>
              <AlertCircle size={48} />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
              {isIncomplete ? "Verification Required" : "Application Under Review"}
            </h2>
            <p style={{ color: '#4b5563', lineHeight: '1.6' }}>
              {isIncomplete
                ? "To view assistance requests, your membership status must be verified. Please complete your profile to unlock this feature."
                : "Your membership application is currently under review. You will be able to view requests once your membership is approved."}
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              {isIncomplete ? (
                <button
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  View Application
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Request & Assistance" user={user} onLogout={onLogout}>
      <div className="request-status-page">
        <div className="request-status__content-layout">
          <div className="request-status__main-column">
            <div className="request-status__categories-header request-status__categories-header--outside">
              <h3>View Your Submitted Requests</h3>
              <button
                type="button"
                className="request-status__header-button"
                onClick={() => navigate('/request-assistance')}
              >
                Request & Assistance
              </button>
            </div>

            <section className="request-status__request-panel">

            {loading && (
              <div className="request-status__state">
                <div className="request-status__spinner" />
                <p>Loading your requests...</p>
              </div>
            )}
            {error && !loading && (
              <div className="request-status__state is-error">
                <AlertCircle size={24} />
                <p>{error}</p>
              </div>
            )}

            {!loading && !error && items.length === 0 && (
              <div className="request-status__empty">
                <div className="request-status__empty-icon">
                  <FileText size={32} />
                </div>
                <h3>No Requests Yet</h3>
                <p>Your submitted assistance requests will appear here. Start by submitting a new request.</p>
              </div>
            )}

            {!loading && !error && items.length > 0 && (
              <>
                <div className="request-status__list">
                  {items.map((request) => {
                  const isDeleted = request.statusKey === 'deleted';
                  const ticketId = request.requestId || request.ticketId || request.id;

                  return (
                    <article
                      key={request.requestId}
                      className={`request-status__card${isDeleted ? ' request-status__card--deleted' : ''}`}
                    >
                      <div className="request-status__card-content">
                        <div className="request-status__card-header">
                          <div className="request-status__card-header-left">
                            <span className="request-status__card-ticket">
                              {request.ticketNo || `#${request.requestId}`}
                            </span>
                            <h3 className="request-status__card-title">{request.subject}</h3>
                          </div>
                          <div className="request-status__card-badges">
                            {request.priorityLabel && (
                              <span className={`request-status__badge request-status__badge--${request.priorityTone}`}>
                                {request.priorityLabel}
                              </span>
                            )}
                            <span className={`request-status__badge request-status__badge--${request.statusTone}`}>
                              {request.statusLabel}
                            </span>
                          </div>
                        </div>

                        <div className="request-status__card-meta">
                          {request.category && (
                            <div className="request-status__card-meta-item">
                              <span className="request-status__card-label">Category:</span>
                              <span className="request-status__card-value">{toTitleCase(request.category)}</span>
                            </div>
                          )}
                          <div className="request-status__card-meta-item">
                            <span className="request-status__card-label">Submitted:</span>
                            <span className="request-status__card-value">{formatDate(request.createdAt)}</span>
                          </div>
                          {request.department && (
                            <div className="request-status__card-meta-item">
                              <span className="request-status__card-label">Department:</span>
                              <span className="request-status__card-value">{request.department}</span>
                            </div>
                          )}
                        </div>

                        {request.latestMessage && (
                          <p className="request-status__card-preview">
                            <MessageSquare size={14} />
                            {request.latestMessage}
                          </p>
                        )}

                        <div className="request-status__card-actions">
                          <button
                            type="button"
                            className="request-status__action request-status__action--compact request-status__action--secondary"
                            onClick={() => navigate(`/my-requests/${ticketId}`)}
                          >
                            <Eye size={16} />
                            View Conversation
                          </button>
                          {!isDeleted && (
                            <button
                              type="button"
                              className="request-status__action request-status__action--compact request-status__action--danger"
                              onClick={() => openDeleteDialog(request)}
                            >
                              <Trash2 size={16} />
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                  })}
                  <p className="request-status__note">
                    Support will review each request and update the status as it progresses.
                  </p>
                </div>
              </>
            )}
            </section>
          </div>

          <aside className="request-status__side-container" aria-label="Additional content panel">
            <h3>What Happens Next?</h3>
            <ul className="request-status__side-list">
              <li>
                <span>Your submitted requests stay visible here with live status updates.</span>
              </li>
              <li>
                <span>Open a request conversation to view admin responses and progress notes.</span>
              </li>
              <li>
                <span>Delete only if you no longer need the request to be processed.</span>
              </li>
              <li>
                <span>For new concerns, return to Request & Assistance and submit a new ticket.</span>
              </li>
            </ul>
          </aside>
          </div>

        {deleteDialog.open && (
          <div
            className="request-status__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
          >
            <div className="request-status__modal-backdrop" onClick={closeDeleteDialog} />
            <div className="request-status__modal-panel request-status__modal-panel--sm">
              <header className="request-status__modal-header">
                <h3 id="delete-dialog-title">Delete request?</h3>
                <button
                  type="button"
                  onClick={closeDeleteDialog}
                  className="request-status__icon-button"
                  aria-label="Close delete dialog"
                >
                  <X size={16} />
                </button>
              </header>
              <div className="request-status__modal-body">
                <p className="request-status__modal-text">
                  Are you sure you want to delete this request?
                </p>
                <p className="request-status__modal-subtext">
                  This action can be undone by contacting support.
                </p>
                {deleteError && <div className="request-status__state is-error">{deleteError}</div>}
              </div>
              <footer className="request-status__modal-footer">
                <button
                  type="button"
                  className="request-status__action request-status__action--secondary"
                  onClick={closeDeleteDialog}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="request-status__action request-status__action--danger"
                  onClick={confirmDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

MyRequestsPage.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  onLogout: PropTypes.func,
};

MyRequestsPage.defaultProps = {
  user: null,
  onLogout: null,
};
